import React, { useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import type { EndSeasonPreview } from '@shootout-comps/core';
import type { League } from '@shootout-comps/types';
import { useLang } from '@shootout-comps/i18n';

interface EndSeasonDialogProps {
  league: League;
  onClose: () => void;
  onCommitted: () => void;
}

type Counts = Record<string, { promotionSpots: number; relegationSpots: number }>;

// Owner/admin-only 3-step wizard: (1) season label + per-division promotion/relegation counts,
// pre-filled from each Division's stored defaults but editable just for this season: (2) preview
// — computed final standings + proposed moves, each individually removable before committing;
// (3) commit — archives the season and applies whichever moves the organizer kept. Modal pattern
// mirrors ManageLeagueAdminsDialog.tsx.
export default function EndSeasonDialog({ league, onClose, onCommitted }: EndSeasonDialogProps) {
  const { previewEndSeason, commitEndSeason } = useLeague();
  const { t } = useLang();

  const [step, setStep] = useState<1 | 2>(1);
  const [seasonLabel, setSeasonLabel] = useState('');
  const [counts, setCounts] = useState<Counts>(() =>
    Object.fromEntries(league.divisions.map(d => [d._id, { promotionSpots: d.promotionSpots ?? 0, relegationSpots: d.relegationSpots ?? 0 }])));
  const [preview, setPreview] = useState<EndSeasonPreview | null>(null);
  const [excludedMoveKeys, setExcludedMoveKeys] = useState<Set<string>>(new Set());
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');

  const moveKey = (divisionId: string, teamId: string) => `${divisionId}:${teamId}`;

  const runPreview = async () => {
    setLoadingPreview(true); setError('');
    try {
      const overrides = league.divisions.map(d => ({ divisionId: d._id, ...counts[d._id] }));
      const result = await previewEndSeason(league._id, overrides);
      setPreview(result);
      setExcludedMoveKeys(new Set());
      setStep(2);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to compute preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCommit = async () => {
    if (!preview || !seasonLabel.trim()) return;
    setCommitting(true); setError('');
    try {
      const divisions = preview.divisions.map(d => ({
        divisionId: d.divisionId,
        promotionSpots: d.promotionSpots,
        relegationSpots: d.relegationSpots,
        applyMoves: d.proposedMoves
          .filter(m => !excludedMoveKeys.has(moveKey(d.divisionId, m.teamId)))
          .map(m => ({ teamId: m.teamId, toDivisionId: m.toDivisionId })),
      }));
      await commitEndSeason(league._id, seasonLabel.trim(), divisions);
      onCommitted();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to end season');
    } finally {
      setCommitting(false);
    }
  };

  const inputStyle: React.CSSProperties = { width: '64px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.3rem 0.4rem', color: 'var(--chalk)', fontSize: '0.8rem', textAlign: 'center', outline: 'none' };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', animation: 'fadeInEndSeason 0.15s ease',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--baize), var(--felt))',
        border: '2px solid rgba(201,168,76,0.4)', borderRadius: '14px',
        padding: 'clamp(1.5rem,5vw,2rem)', width: '100%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)', animation: 'slideUpEndSeason 0.2s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', lineHeight: 1, color: 'var(--gold)' }}>
            {t.endSeasonBtn}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '1.3rem', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', marginBottom: '1.1rem' }}>{league.name}</p>

        {error && (
          <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: '#e74c3c', fontSize: '0.78rem', marginBottom: '0.9rem' }}>
            {error}
          </div>
        )}

        {step === 1 ? (
          <>
            <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--chalk-dim)', marginBottom: '0.4rem' }}>{t.seasonLabelFieldLabel}</label>
            <input
              value={seasonLabel}
              onChange={e => setSeasonLabel(e.target.value)}
              placeholder={t.seasonLabelPlaceholder}
              style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: 'var(--chalk)', fontSize: '0.85rem', outline: 'none', marginBottom: '1.1rem', boxSizing: 'border-box' }}
            />

            <p style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', marginBottom: '0.6rem' }}>
              {t.seasonCountsHint}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
              {league.divisions.map(d => (
                <div key={d._id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.6rem 0.8rem' }}>
                  <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600 }}>{d.name} <span style={{ color: 'var(--chalk-dim)', fontWeight: 400, fontSize: '0.72rem' }}>({t.tierLabel} {d.tier ?? 1})</span></span>
                  <label style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)' }}>↑</label>
                  <input
                    type="number" min={0}
                    value={counts[d._id]?.promotionSpots ?? 0}
                    onChange={e => setCounts(prev => ({ ...prev, [d._id]: { ...prev[d._id], promotionSpots: Math.max(0, Number(e.target.value) || 0) } }))}
                    style={inputStyle}
                  />
                  <label style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)' }}>↓</label>
                  <input
                    type="number" min={0}
                    value={counts[d._id]?.relegationSpots ?? 0}
                    onChange={e => setCounts(prev => ({ ...prev, [d._id]: { ...prev[d._id], relegationSpots: Math.max(0, Number(e.target.value) || 0) } }))}
                    style={inputStyle}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={onClose} style={{ flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.6rem', color: 'var(--chalk-dim)', cursor: 'pointer' }}>{t.cancel}</button>
              <button onClick={runPreview} disabled={loadingPreview || !seasonLabel.trim()} style={{ flex: 2, background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.6rem', fontWeight: 700, cursor: 'pointer', opacity: loadingPreview || !seasonLabel.trim() ? 0.6 : 1 }}>
                {loadingPreview ? '…' : t.previewBtn}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', marginBottom: '0.9rem' }}>
              {t.reviewMovesHint}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.25rem' }}>
              {preview?.divisions.map(d => (
                <div key={d.divisionId} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.75rem 0.9rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.5rem' }}>{d.divisionName}</div>
                  {d.proposedMoves.length === 0 ? (
                    <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', margin: 0 }}>{t.noMovesThisSeason}</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      {d.proposedMoves.map(m => {
                        const key = moveKey(d.divisionId, m.teamId);
                        const included = !excludedMoveKeys.has(key);
                        return (
                          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={included}
                              onChange={() => setExcludedMoveKeys(prev => {
                                const next = new Set(prev);
                                if (included) next.add(key); else next.delete(key);
                                return next;
                              })}
                              style={{ accentColor: 'var(--gold)' }}
                            />
                            <span>{m.direction === 'promote' ? '⬆️' : '⬇️'} {m.teamName}</span>
                            <span style={{ color: 'var(--chalk-dim)' }}>→ {m.toDivisionName}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.6rem', color: 'var(--chalk-dim)', cursor: 'pointer' }}>{t.backBtn}</button>
              <button onClick={handleCommit} disabled={committing} style={{ flex: 2, background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.6rem', fontWeight: 700, cursor: 'pointer', opacity: committing ? 0.6 : 1 }}>
                {committing ? '…' : t.confirmEndSeasonBtn}
              </button>
            </div>
          </>
        )}
      </div>
      <style>{`
        @keyframes fadeInEndSeason  { from{opacity:0} to{opacity:1} }
        @keyframes slideUpEndSeason { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
